import { TokenSelectionWrapper } from '@/components/features/Account/AccountNFTList/TokenSelectionWrapper';
import { useTokenSelection } from '@/components/features/Account/AccountNFTList/useTokenSelection';
import { NftCard } from '@/components/shared/NFTCard';
import NFTCardSkeleton from '@/components/shared/NFTCard/NFTCardSkeleton';
import { UserToken } from '@/hooks/use-user-tokens';
import { TOKEN_QUERY_LIMIT } from '@/utils/getTokenQueryFromParams';
import { BoxProps, SimpleGrid } from '@chakra-ui/react';
import { DynamicTokens } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

type Props = BoxProps & {
  items: (DynamicTokens[number] & { ownership?: UserToken['ownership'] })[];
  isLoading?: boolean;
  variant?: Variants;
  isOwner?: boolean;
  mutate?: () => void;
};

const NFTList = ({ items, isLoading, variant, isOwner, mutate, ...boxProps }: Props) => {
  const resetTokenSelection = useTokenSelection(state => state.reset);
  const { t } = useTranslation('search');

  const [playingElement, setPlayingElement] = useState<
    HTMLAudioElement | HTMLVideoElement | null
  >();

  const columns = getGridSizeByVariant(variant);

  /* Reset the token selection state when the NFTList isOwner prop changes or if the component unmounts. */
  useEffect(() => {
    resetTokenSelection();

    return () => {
      resetTokenSelection();
    };
  }, [isOwner, resetTokenSelection]);

  return (
    <SimpleGrid columns={columns} spacing={{ base: 'space.16', md: 'space.24' }} {...boxProps}>
      {isLoading
        ? [...Array(TOKEN_QUERY_LIMIT)].map((_, index) => (
            <NFTCardSkeleton key={`loading-card-${index}`} />
          ))
        : items.map(token => {
            const id = `${token.token?.contract}:${token.token?.tokenId}`;
            const tokensOwned = Number(token.ownership?.tokenCount || 1);

            return (
              <TokenSelectionWrapper
                key={id}
                id={token.token?.tokenId || ''}
                contractAddress={token.token?.contract || ''}
                kind={token.token?.kind?.includes('721') ? 721 : 1155}
                tokensOwned={tokensOwned}
                isEnabled={isOwner}
              >
                <NftCard
                  token={token}
                  showCollectionName={variant === Variants.Profile}
                  mutate={mutate}
                  onMediaPlayed={e => {
                    if (playingElement && playingElement !== e.nativeEvent.target) {
                      playingElement.pause();
                    }

                    const element = e.nativeEvent.target as HTMLAudioElement | HTMLVideoElement;

                    if (element) {
                      setPlayingElement(element);
                    }
                  }}
                  buyEnabled
                  listEnabled
                  isUserOverview={isOwner}
                  quantityLabel={
                    variant === Variants.Profile
                      ? {
                          value: Number(token.ownership?.tokenCount || 1),
                          prefix: t('results.owned-by-user'),
                        }
                      : { value: token.token?.remainingSupply || 1 }
                  }
                />
              </TokenSelectionWrapper>
            );
          })}
    </SimpleGrid>
  );
};

export enum Variants {
  More = 'more',
  Profile = 'profile',
}

export const getGridSizeByVariant = (variant?: Variants) => {
  switch (variant) {
    case Variants.Profile:
      return { base: 2, md: 3, lg: 4, '2xl': 5, '3xl': 6 };

    // Note that this variant is used by components where we can only retrieve a fixed amount of list items, meaning we cannot
    // adjust the elastic query based on the viewport width. Therefor a fixed unit must be used from 'md' and up
    case Variants.More:
      return { base: 2, md: 4 };
    default:
      return { base: 2, lg: 3, xl: 4, '3xl': 5 };
  }
};

export default NFTList;
